import React, { useState, useEffect, useRef } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Input } from "./ui/input";
import { cn } from "@/lib/utils";

// ----------------- Conversion helpers -----------------
// Value on the wire is always ISO YYYY-MM-DD. The visible field always shows
// DD/MM/YYYY regardless of browser locale.
const isoToDisplay = (iso) => {
    if (!iso) return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return "";
    return `${m[3]}/${m[2]}/${m[1]}`;
};

const displayToIso = (txt) => {
    if (!txt) return "";
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(txt.trim());
    if (!m) return null; // signal invalid
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
    if (isNaN(d.getTime()) || d.getUTCDate() !== Number(dd) || d.getUTCMonth() + 1 !== Number(mm)) {
        return null;
    }
    return `${yyyy}-${mm}-${dd}`;
};

const isoToDate = (iso) => {
    if (!iso) return undefined;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!m) return undefined;
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

const dateToIso = (d) => {
    if (!d) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

// Auto-insert slashes as the user types (12 → 12/, 1207 → 12/07/, ...).
function formatWhileTyping(raw) {
    const digits = raw.replace(/\D/g, "").slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    return out;
}

// ------------------------------------------------------------------
// DateInput
//   value    — ISO string "YYYY-MM-DD" (or "" / null / undefined)
//   onChange — receives the same ISO string (fires on valid entry / picker)
//   min/max  — optional ISO strings
//   Any extra prop (className, required, disabled, data-testid, id, name) is
//   passed through to the text input so form logic keeps working.
// ------------------------------------------------------------------
export default function DateInput({
    value,
    onChange,
    className,
    disabled,
    required,
    min,
    max,
    placeholder = "dd/mm/yyyy",
    id,
    name,
    "data-testid": dataTestId,
    ...rest
}) {
    const [text, setText] = useState(isoToDisplay(value));
    const [open, setOpen] = useState(false);
    const [invalid, setInvalid] = useState(false);
    const lastEmitted = useRef(value || "");

    // Keep text in sync when parent-supplied `value` changes (e.g. reset).
    useEffect(() => {
        if ((value || "") !== lastEmitted.current) {
            setText(isoToDisplay(value));
            setInvalid(false);
        }
    }, [value]);

    const emit = (iso) => {
        lastEmitted.current = iso || "";
        onChange?.(iso || "");
    };

    const handleTyping = (e) => {
        const shaped = formatWhileTyping(e.target.value);
        setText(shaped);
        // Only emit once user has typed a complete 10-char DD/MM/YYYY.
        if (shaped.length === 10) {
            const iso = displayToIso(shaped);
            if (iso === null) {
                setInvalid(true);
            } else {
                setInvalid(false);
                emit(iso);
            }
        } else {
            // Partial input: don't emit anything, but clear "invalid" style.
            setInvalid(false);
            if (shaped === "" && value) emit("");
        }
    };

    const handleBlur = () => {
        if (!text) return;
        const iso = displayToIso(text);
        if (iso === null) setInvalid(true);
    };

    const pickFromCalendar = (d) => {
        const iso = dateToIso(d);
        setText(isoToDisplay(iso));
        setInvalid(false);
        emit(iso);
        setOpen(false);
    };

    return (
        <div className={cn("relative inline-flex items-center", className)}>
            <Input
                type="text"
                inputMode="numeric"
                pattern="\d{2}/\d{2}/\d{4}"
                value={text}
                onChange={handleTyping}
                onBlur={handleBlur}
                placeholder={placeholder}
                disabled={disabled}
                required={required}
                id={id}
                name={name}
                data-testid={dataTestId}
                aria-invalid={invalid || undefined}
                className={cn(
                    "pr-8",
                    invalid && "border-rose-500 focus-visible:ring-rose-400",
                )}
                {...rest}
            />
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        aria-label="Open calendar"
                        disabled={disabled}
                        className="absolute right-2 inline-flex items-center justify-center h-6 w-6 rounded hover:bg-slate-100 disabled:opacity-40"
                        data-testid={dataTestId ? `${dataTestId}-picker` : undefined}
                    >
                        <CalendarIcon className="h-4 w-4 text-slate-500" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="end"
                    // Iter19 — Fix Safari-in-Dialog: shadcn Dialog overlay/content
                    // both use z-50; a popover at z-50 stacks under (or at parity
                    // with) the dialog overlay depending on DOM insertion order,
                    // which on Safari causes calendar day clicks to be swallowed
                    // by the dialog overlay. Bumping to z-[60] guarantees the
                    // calendar is above the overlay. Also prevent the popover
                    // from stealing focus back into the dialog's focus trap on
                    // close (which was flashing the picked date back to blank).
                    className="w-auto p-0 z-[60] pointer-events-auto"
                    onOpenAutoFocus={(e) => e.preventDefault()}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                >
                    <Calendar
                        mode="single"
                        selected={isoToDate(value)}
                        onSelect={pickFromCalendar}
                        defaultMonth={isoToDate(value) || new Date()}
                        // Iter56 — allow selecting DOBs back to 1930 by default.
                        // Callers can still override fromYear/toYear via props.
                        fromYear={rest.fromYear ?? 1930}
                        toYear={rest.toYear ?? (new Date().getFullYear() + 20)}
                        disabled={(d) => {
                            const iso = dateToIso(d);
                            if (min && iso < min) return true;
                            if (max && iso > max) return true;
                            return false;
                        }}
                        initialFocus
                    />
                    {/* Iter56 — footer with Today / Clear + current-value display. */}
                    <div className="flex items-center justify-between gap-2 px-3 py-2 border-t border-slate-200 text-xs">
                        <span className="mono text-slate-500"
                              data-testid={dataTestId ? `${dataTestId}-footer-value` : undefined}>
                            {value ? isoToDisplay(value) : "no date selected"}
                        </span>
                        <div className="flex items-center gap-1">
                            <button
                                type="button"
                                onClick={() => pickFromCalendar(new Date())}
                                className="px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50"
                                data-testid={dataTestId ? `${dataTestId}-today` : undefined}
                            >Today</button>
                            <button
                                type="button"
                                onClick={() => { setText(""); setInvalid(false); emit(""); setOpen(false); }}
                                className="px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-600"
                                data-testid={dataTestId ? `${dataTestId}-clear` : undefined}
                            >Clear</button>
                        </div>
                    </div>
                </PopoverContent>
            </Popover>
        </div>
    );
}
